import { useState, type FormEvent } from 'react';
import { saveSubcategory } from '@/modules/inventory/services';
import { ActiveBadge } from '@/modules/inventory/components';
import { useCategories, useSubcategories } from '@/modules/inventory/hooks';
import type { Subcategory, SubcategoryInput } from '@/modules/inventory/types';
import { useAuth } from '@/shared/hooks/useAuth';
import {
  CATALOG_RULES_DEPLOY_HINT,
  shouldShowCatalogDeployHint,
} from '@/shared/utils/firestoreError';

const empty: SubcategoryInput = {
  categoryId: '',
  name: '',
  description: '',
  active: true,
};

export function SubcategoriesPage() {
  const { categories } = useCategories();
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const { subcategories, loading, error, refresh } = useSubcategories(
    filterCategoryId || undefined,
  );
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('catalog.write');
  const showDeployHint = shouldShowCatalogDeployHint(profile);

  const [form, setForm] = useState<SubcategoryInput>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const categoryName = (id: string) =>
    categories.find((c) => c.id === id)?.name ?? id;

  function startEdit(sub: Subcategory) {
    setEditingId(sub.id);
    setForm({
      categoryId: sub.categoryId,
      name: sub.name,
      description: sub.description,
      active: sub.active,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm({ ...empty, categoryId: filterCategoryId || '' });
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !form.categoryId || !form.name.trim()) {
      setFormError('Categoría y nombre son obligatorios.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveSubcategory(form, editingId ?? undefined, profile);
      resetForm();
      await refresh();
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : 'No se pudo guardar. Verificá permisos y reglas de Firestore.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h1>Subcategorías</h1>

      <label className="auth-label">
        Filtrar por categoría
        <select
          value={filterCategoryId}
          onChange={(e) => setFilterCategoryId(e.target.value)}
        >
          <option value="">Todas</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      {canWrite ? (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Editar' : 'Nueva'} subcategoría</h2>
          <label className="auth-label">
            Categoría *
            <select
              required
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            >
              <option value="">— Elegir —</option>
              {categories
                .filter((c) => c.active)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="auth-label">
            Nombre *
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Descripción
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Activa
          </label>
          {formError ? (
            <>
              <p className="auth-error">{formError}</p>
              {showDeployHint ? (
                <div
                  className="auth-muted"
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #ccc)',
                    borderRadius: '4px',
                  }}
                >
                  <strong>Desbloquear catálogo (Fase 0)</strong>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.9em' }}>
                    {CATALOG_RULES_DEPLOY_HINT}
                  </p>
                </div>
              ) : null}
            </>
          ) : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              Guardar
            </button>
            {editingId ? (
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={resetForm}
              >
                Cancelar
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      <h2>Listado</h2>
      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}
      <table className="users-table">
        <thead>
          <tr>
            <th>Categoría</th>
            <th>Nombre</th>
            <th>Estado</th>
            {canWrite ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {subcategories.map((sub) => (
            <tr key={sub.id}>
              <td>{categoryName(sub.categoryId)}</td>
              <td>{sub.name}</td>
              <td>
                <ActiveBadge active={sub.active} />
              </td>
              {canWrite ? (
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => startEdit(sub)}
                  >
                    Editar
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
